/**
 * `/support/:id` — case detail (§11.4): evidence timeline (claim states, evidence kinds, verification results —
 * never ID documents), notes, information requests, external-channel follow-up records and the Appendix A status
 * machine. Every change is audited by the API.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SocialPlatform,
  SupportCaseMachine,
  SupportPriority,
  type SupportCaseStatus,
  type UpdateSupportCaseRequest,
} from '@windrop/contracts';
import { Button, Field, Input, Select, Skeleton, useIntl, useToast } from '@windrop/ui';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useSession } from '../../auth/session';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { Timeline } from '../../components/Timeline';
import { supportApi } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';

const PRIORITY_TONE = { URGENT: 'error', HIGH: 'warning', NORMAL: 'info', LOW: 'neutral' } as const;
const KIND_TONE = {
  CASE: 'neutral',
  CLAIM: 'info',
  WIN: 'success',
  EVIDENCE: 'neutral',
  VERIFICATION: 'warning',
  FULFILLMENT: 'info',
  FOLLOW_UP: 'neutral',
} as const;

export function SupportCaseDetailPage() {
  const { id = '' } = useParams();
  const intl = useIntl();
  const { toast } = useToast();
  const { session } = useSession();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['support', 'case', id],
    queryFn: () => supportApi.get(id),
    retry: false,
  });
  const [text, setText] = useState('');
  const [kind, setKind] = useState<
    'internalNote' | 'participantMessage' | 'requestInformation' | 'externalFollowUp'
  >('internalNote');
  const [platform, setPlatform] = useState<SocialPlatform>('WHATSAPP');
  const [nextUpdateAt, setNextUpdateAt] = useState('');

  const update = useMutation({
    mutationFn: (body: UpdateSupportCaseRequest) => supportApi.update(id, body),
    onSuccess: () => {
      toast({ title: 'Case updated', tone: 'success' });
      setText('');
      setNextUpdateAt('');
      void queryClient.invalidateQueries({ queryKey: ['support'] });
    },
    onError: (e) =>
      toast({
        title: 'Update failed',
        description: e instanceof Error ? e.message : undefined,
        tone: 'neutral',
      }),
  });

  const submitNote = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    const body: UpdateSupportCaseRequest =
      kind === 'externalFollowUp'
        ? { externalFollowUp: { platform, summary: text.trim() } }
        : { [kind]: text.trim() };
    if (nextUpdateAt) body.nextUpdateAt = new Date(nextUpdateAt).toISOString();
    update.mutate(body);
  };

  const c = query.data;
  const nextStates = c ? SupportCaseMachine.nextStates(c.status) : [];

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Support cases', to: '/support' }, { label: c?.caseRef ?? 'Case' }]}
        title={c?.caseRef ?? 'Support case'}
        description={c ? humanizeStatus(c.category) : undefined}
      />
      {query.isPending ? <Skeleton shape="block" height={240} label="Loading case" /> : null}
      {query.isError ? (
        isApiError(query.error) && query.error.status === 404 ? (
          <EmptyState
            title="No such case in your tenant"
            body="The reference is unknown here; the console never guesses."
            action={<Button href="/support">Back to support cases</Button>}
          />
        ) : (
          <ErrorState
            title="Could not load the case"
            body="Retry in a moment."
            reference={isApiError(query.error) ? query.error.supportReference : undefined}
            action={<Button onClick={() => query.refetch()}>Retry</Button>}
          />
        )
      ) : null}
      {c ? (
        <div className="wdc-grid" data-cols="2">
          <section className="wdc-panel">
            <h2>Case</h2>
            <dl className="wdc-facts">
              <div>
                <dt>Status</dt>
                <dd>
                  <StatusBadge kind="SupportCaseStatus" value={c.status} />
                </dd>
              </div>
              <div>
                <dt>Priority</dt>
                <dd>
                  <ToneBadge tone={PRIORITY_TONE[c.priority]}>{humanizeStatus(c.priority)}</ToneBadge>{' '}
                  {c.escalated ? '· escalated' : ''}
                </dd>
              </div>
              <div>
                <dt>Owner</dt>
                <dd>
                  {c.assignedTo ? c.owner : 'Unassigned'}{' '}
                  {session && c.assignedTo !== session.staffId ? (
                    <Button variant="quiet" onClick={() => update.mutate({ assignTo: session.staffId })}>
                      Assign to me
                    </Button>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt>Next update</dt>
                <dd data-testid="case-next-update">
                  {c.nextUpdateAt ? intl.formatDateTime(c.nextUpdateAt) : '—'}{' '}
                  {c.slaBreached ? <ToneBadge tone="error">Overdue</ToneBadge> : null}
                </dd>
              </div>
              <div>
                <dt>Appeal</dt>
                <dd>{c.appealPath ?? '—'}</dd>
              </div>
              <div>
                <dt>Recovery</dt>
                <dd>
                  {c.recoveryRequested
                    ? 'Requested — identity through the approved recovery flow; a win ID alone is never proof.'
                    : '—'}
                </dd>
              </div>
              <div>
                <dt>Linked</dt>
                <dd>
                  {c.links.winId ? (
                    <Link to={`/winners/lookup/${c.links.winId}`}>
                      <code>{c.links.winId}</code>
                    </Link>
                  ) : null}
                  {c.links.claimId ? <div className="wd-muted">claim {c.links.claimId}</div> : null}
                  {c.links.entryId ? <div className="wd-muted">entry {c.links.entryId}</div> : null}
                  {c.links.paymentId ? <div className="wd-muted">payment {c.links.paymentId}</div> : null}
                  {!c.links.winId && !c.links.claimId && !c.links.entryId && !c.links.paymentId ? '—' : null}
                </dd>
              </div>
            </dl>
            <div className="wd-btn-group">
              {nextStates.map((s) => (
                <Button
                  key={s}
                  variant={s === 'RESOLVED' ? 'primary' : 'secondary'}
                  onClick={() => update.mutate({ status: s as SupportCaseStatus })}
                  loading={update.isPending}
                >
                  {humanizeStatus(s)}
                </Button>
              ))}
            </div>
            <Field label="Priority">
              <Select
                options={SupportPriority.options.map((p) => ({ value: p, label: humanizeStatus(p) }))}
                value={c.priority}
                onChange={(e) => update.mutate({ priority: e.target.value as SupportPriority })}
              />
            </Field>
          </section>
          <section className="wdc-panel">
            <h2>Add to the case</h2>
            <form onSubmit={submitNote} className="wd-stack">
              <Field label="Type">
                <Select
                  options={[
                    { value: 'internalNote', label: 'Internal note' },
                    { value: 'requestInformation', label: 'Request information from the participant' },
                    { value: 'participantMessage', label: 'Message to the participant' },
                    { value: 'externalFollowUp', label: 'Record an external-channel follow-up' },
                  ]}
                  value={kind}
                  onChange={(e) => setKind(e.target.value as typeof kind)}
                />
              </Field>
              {kind === 'externalFollowUp' ? (
                <Field label="Platform">
                  <Select
                    options={SocialPlatform.options.map((p) => ({ value: p, label: humanizeStatus(p) }))}
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value as SocialPlatform)}
                  />
                </Field>
              ) : null}
              <Field
                label="Text"
                hint={
                  kind === 'participantMessage' || kind === 'requestInformation'
                    ? 'Participant-visible: use the Appendix A safe wording; name only the item you need.'
                    : 'Internal — never visible to the participant.'
                }
              >
                <Input value={text} onChange={(e) => setText(e.target.value)} data-testid="case-text" />
              </Field>
              <Field label="Next update by">
                <Input
                  type="datetime-local"
                  value={nextUpdateAt}
                  onChange={(e) => setNextUpdateAt(e.target.value)}
                />
              </Field>
              <Button type="submit" variant="secondary" loading={update.isPending} disabled={!text.trim()}>
                Add
              </Button>
            </form>
          </section>
          <section className="wdc-panel wdc-span-all">
            <h2>Evidence timeline</h2>
            <p className="wd-muted">
              Claim states, evidence kinds and decisions only — documents stay under restricted access.
            </p>
            <Timeline
              events={c.timeline.map((t, i) => ({
                id: `${t.kind}-${i}`,
                at: t.at,
                title: t.title,
                detail: t.detail ?? undefined,
                tone: KIND_TONE[t.kind],
              }))}
              emptyText="No activity yet."
            />
          </section>
          <section className="wdc-panel">
            <h2>Participant-visible messages</h2>
            <Timeline
              events={c.messages.map((m, i) => ({
                id: `m-${i}`,
                at: m.at,
                title: m.text,
                actor: m.from === 'PARTICIPANT' ? 'Participant' : 'Support',
              }))}
              emptyText="No messages."
            />
          </section>
          <section className="wdc-panel">
            <h2>Internal notes and follow-ups</h2>
            <Timeline
              events={c.internalNotes.map((n, i) => ({
                id: `n-${i}`,
                at: n.at,
                title: n.text,
                actor: n.staffId,
              }))}
              emptyText="No internal notes."
            />
          </section>
        </div>
      ) : null}
    </>
  );
}
